import { PushpinOutlined } from "@ant-design/icons";
import { Modal, Tooltip } from "antd";
import { TRANSFER_INBOX } from "constants";
import { AuthContext } from "context/auth/authentication";
import { logout } from "hooks/api/logoutApi";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import ToastMsg from "components/common/ToastMsg";
import MyButton from "components/ui/Button/MyButton";
import { TRANSFER_INBOX_PING } from "constants";
import { EmployeeDetails } from "components/common/CommonComponent";
import dayjs from "dayjs";

const TransferRMPing = () => {
    const is_reporting_manager = JSON.parse(sessionStorage.getItem("reportingManager"));
    const employee_id = JSON.parse(sessionStorage.getItem("employee_id"));
    const authtoken = sessionStorage.getItem("token");

    const { logoutauth } = useContext(AuthContext);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isConfirmed, setIsConfirmed] = useState(false);
    const [countdown, setCountdown] = useState(15);
    const [data, setData] = useState(null)
    const [count, setCount] = useState(null)

    const timerRef = useRef(null);

    const showModal = () => {
        setIsModalOpen(true);
        setIsConfirmed(false);
        setCountdown(15);
    };

    const handleCancel = () => {
        setIsModalOpen(false);
        setIsConfirmed(false);
        setCountdown(15);
    };

    const handleConfirm = () => {
        setIsConfirmed(true); // Show confirmation message

        // Start countdown
        timerRef.current = setInterval(async () => {
            setCountdown((prev) => {
                if (prev <= 1) {
                    clearInterval(timerRef.current);

                    // Call logout API
                    (async () => {
                        try {
                            const data = await logout();
                            if (data?.status === "success") {
                                logoutauth();
                                window.location.pathname = '/';
                            } else {
                                ToastMsg("warning", data.message || "Logout failed");
                            }
                        } catch (err) {
                            console.error("Logout error", err);
                            ToastMsg("error", "Something went wrong");
                        }
                    })();

                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    // Optional: Cleanup on unmount
    useEffect(() => {
        return () => clearInterval(timerRef.current);
    }, []);

    const fetchTransferRequest = async () => {
        const requestOptions = {
            method: "GET",
            headers: { Authorization: `token ${authtoken}` },
        };
        const res = await fetch(TRANSFER_INBOX_PING, requestOptions)
        const data = await res.json()
        if (data) {
            if (data?.status === 'success') {
                const requests = data?.data || [];
                setData(requests);
                setCount(requests.length);
            }
        }
    }

    useEffect(() => {
        if (authtoken) {
            fetchTransferRequest()
        }
    }, []);

    const handleAction = (status, id) => {
        try {
            let updatedFormData = {
                "status": status,
                "transfer_id": id
            };
            fetch(TRANSFER_INBOX, {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `token ${authtoken}`
                },
                body: JSON.stringify(updatedFormData)
            })
                .then((response) => response.json())
                .then(async data => {
                    if (data.status === "success") {
                        ToastMsg("success", data.message);
                        handleConfirm()
                    }
                    else if (data.status === "fail") {
                        ToastMsg("error", data.message);
                        handleCancel()
                    }
                })
                .catch(error => {
                    ToastMsg("error", error.message);
                });
        } catch (error) {
            ToastMsg("error", error.message);
        }
    }

    return (
        <>
            {is_reporting_manager && employee_id && authtoken && count > 0 && (
                <div className="fixed bottom-6 right-6 z-50 group">
                    {/* Badge count */}
                    {count > 0 && (
                        <div className="absolute -top-1 -right-1 bg-red-600 text-white text-xs font-bold px-1.5 py-0.5 rounded-full">
                            {count > 9 ? "9+" : count}
                        </div>
                    )}

                    {/* pin */}
                    <Tooltip placement="leftBottom" title="Transfer Request">
                        <button
                            onClick={showModal}
                            className="bg-[#04b7b1] hover:bg-[#247978] text-white p-2 rounded-full shadow-lg flex items-center justify-center"
                            title="Transfer Inbox"
                        >
                            <PushpinOutlined className="text-xl" />
                        </button>
                    </Tooltip>
                </div>
            )}

            <Modal
                title={!isConfirmed ? `Transfer Authority Requests (${count})` : "Authority Transfer Successful"}
                open={isModalOpen}
                onCancel={handleCancel}
                closable={!isConfirmed}
                maskClosable={!isConfirmed}
                footer={false}
                width={500}
            >
                {!isConfirmed ? (
                    <div className="space-y-4 max-h-96 overflow-y-auto">
                        {data?.map((item, idx) => {
                            // employee data
                            const record = item?.sent_from_employee ?? {};
                            const empDetails = {
                                ...record,
                                title: record.title || "",
                                first_name: record.employee_name || "",
                                last_name: "",
                                designation_id: {
                                    id: record.designation?.id || "",
                                    designation_name: record.designation?.name || ""
                                },
                                id: record.employee_id || "",
                                profile: record.profile || "",
                            };

                            return (
                                <div key={item.id} className="bg-white rounded-xl shadow-sm p-4 border border-gray-200">
                                    <EmployeeDetails details={empDetails} />

                                    <div className="mt-3 space-y-3 text-gray-700 text-xs">
                                        <div className="flex items-center gap-2">
                                            <span className="font-medium text-gray-600 w-40">Request #{idx + 1} Submitted On:</span>
                                            <span className="text-gray-900">{dayjs(item?.date).format("DD MMM YYYY")}</span>
                                        </div>

                                        <div className="flex items-center gap-2">
                                            <span className="font-medium text-gray-600 w-40">Date Range:</span>
                                            <span className="text-gray-900">
                                                {dayjs(item?.from_date).format("DD MMM YYYY")} → {dayjs(item?.to_date).format("DD MMM YYYY")}
                                            </span>
                                        </div>

                                        <div className="flex items-start gap-2">
                                            <span className="font-medium text-gray-600 w-40">Reason:</span>
                                            <p className="text-gray-900 whitespace-pre-line">{item?.comments}</p>
                                        </div>

                                        <div className="flex justify-end gap-2 mt-4">
                                            <MyButton label="Reject" outlined onClick={() => handleAction("rejected", item.id)} />
                                            <MyButton label="Confirm" onClick={() => handleAction("approved", item.id)} />
                                        </div>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                ) : (
                    <div className="text-center">
                        <p className="text-sm text-gray-700">
                            You have been assigned as the new <strong>Reporting Manager</strong>.<br />
                            Your current session will expire in <strong>{countdown} second{countdown !== 1 ? "s" : ""}</strong>.
                        </p>
                        <p className="mt-2 text-sm text-gray-500">
                            Please wait while we log you out and redirect you to the login screen to complete the transition.
                        </p>
                    </div>
                )}
            </Modal>
        </>
    );
}

export default TransferRMPing;